/**
 * `<DiceApp>` — the content of **DICE.app**, a player's roll sheet (`dice-rework` Phase 2, the
 * design @ben approved 2026-10-04): every narrative roll their pilot can make, one tap from rolling.
 * A medium rail page; the GM has none (the GM rolls from COMMS, `CommsDice.tsx`).
 *
 * - **The ROLLING bar** holds one loaded roll: its name, bonus and kind, the ACC and DIFF steppers,
 *   the live `/roll` preview and ROLL. ROLL sends **exactly** the preview via `session.postChat`
 *   (the counters and notation are `checkComposer.ts`'s `useCheckComposer`, shared with COMMS's
 *   composer), landing as a normal `/roll` card in COMMS.
 * - **01 PILOT** lists the pilot's owned skill triggers (compendium description, +2 per rank) and
 *   GRIT; **02 MECH** the four HASE tiles (`diceBonusRows.ts`'s `diceSheetRows`). Tapping a row loads
 *   it into the bar; its die (or ROLL NOW) rolls it flat at once through the shared flat roll
 *   (`shell/railDice.ts`'s `useFlatRoll`, the one the PILOT and MECH sheets use), clearing ACC/DIFF.
 *
 * The sheet's pilot is the session's own selected pilot (`session.selectedPilotId`), read off the
 * `roster` prop; with none, the bar still rolls a flat d20 and the sheet says to pick a pilot.
 *
 * **`preselect`** loads a bonus from elsewhere (a COMBAT stat cell, through `openDice`). It is
 * checked for presence, never truthiness of `.bonus`, so a genuine `bonus: 0` still loads, and a
 * NEW object while mounted re-loads (the host hands a fresh one per press).
 */
import { useMemo, useState } from "react";
import { NotSentNote, useNotSentNote } from "../../lib/linkHealth";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { useCheckComposer, type PreselectBonus } from "./checkComposer";
import { composeCheckNotation, labelRoll } from "./checkNotation";
import { DiceStepper } from "./DiceStepper";
import { diceSheetRows, type SheetRow } from "./diceBonusRows";
import { useFlatRoll } from "./shell/railDice";
import "./dice-app.css";

export type { PreselectBonus };

const signed = (n: number) => (n >= 0 ? `+${n}` : `${n}`);

/** The d20's outline: the die on a row's quick-roll button. */
function DieGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
      <path d="M12 2l9 5v10l-9 5-9-5V7z" />
      <path d="M12 2v20M3 7l9 5 9-5" />
    </svg>
  );
}

export function DiceApp({
  roster,
  preselect,
}: {
  /** The shared roster room, read-only here: resolves `session.selectedPilotId` into the sheet's rows. */
  roster?: UseRoster;
  preselect?: PreselectBonus | null;
}) {
  const session = useGameSessionContext();
  const { compendium } = useCompendiumContext();
  const { notSent, report } = useNotSentNote();
  const rollFlat = useFlatRoll();
  const { bonus, accuracy, difficulty, source, notation, pick, setAccuracy, setDifficulty } = useCheckComposer(preselect);
  // What the last roll sent, echoed under the bar until the next one.
  const [sent, setSent] = useState<string | null>(null);

  const pilotId = session.selectedPilotId;
  const pilot = pilotId ? roster?.state?.pilots[pilotId] : undefined;
  const computed = pilot ? roster?.computed[pilot.id] : undefined;
  const rows = useMemo(() => (pilot ? diceSheetRows(pilot, computed, compendium) : null), [pilot, computed, compendium]);
  const loaded = rows ? [...rows.pilot, ...rows.mech].find((r) => r.label === source) : undefined;
  const canRoll = session.actorId !== null;

  const roll = () => {
    if (report(session.postChat(notation))) setSent(notation);
  };
  const quick = (row: SheetRow) => {
    pick(row.label, row.bonus);
    setAccuracy(0);
    setDifficulty(0);
    if (report(rollFlat({ label: row.label, bonus: row.bonus }))) setSent(labelRoll(composeCheckNotation(row.bonus, 0, 0), row.label));
  };

  return (
    <div className="dice-sheet">
      <div className="dice-bar">
        <div className="dice-bar-head">
          <span className="dice-bar-kicker">ROLLING</span>
          <span className="dice-bar-name">{source ?? "Flat d20"}</span>
          <span className="dice-bar-bonus">{signed(bonus)}</span>
          <span className="dice-bar-kind">{loaded?.kind ?? (source ? "CHECK" : "NO BONUS")}</span>
        </div>
        <div className="dice-bar-steps">
          <DiceStepper look="bar" label="Accuracy" short="ACC" value={accuracy} onChange={setAccuracy} min={0} />
          <DiceStepper look="bar" label="Difficulty" short="DIFF" value={difficulty} onChange={setDifficulty} min={0} />
        </div>
        <div className="dice-bar-rollrow">
          <div className="dice-preview" aria-live="polite">
            {notation}
          </div>
          <button type="button" className="dice-bar-roll" disabled={!canRoll} onClick={roll}>
            ROLL
          </button>
        </div>
        <div className="dice-bar-sent">{sent ? `> sent to COMMS · ${sent}` : "Tap a row to load it here; tap its die to roll it flat."}</div>
        <NotSentNote show={notSent} />
      </div>

      {rows === null ? (
        <div className="dice-empty" role="status">
          <div className="dice-empty-title">NO PILOT SELECTED</div>
          <div>Pick your pilot to roll their skill triggers, GRIT and mech skills here. The bar above still rolls a flat d20.</div>
        </div>
      ) : (
        <>
          <SectionHead index="01" title="PILOT" aside="Out of the mech. Pick the trigger that fits." />
          <div className="dice-list">
            {rows.pilot.map((row) => (
              <div key={row.key} className="dice-list-row">
                <button type="button" className="dice-pick" aria-pressed={row.label === source} onClick={() => pick(row.label, row.bonus)}>
                  <span className="dice-pick-text">
                    <span className="dice-pick-name">{row.label}</span>
                    {row.hint && <span className="dice-pick-hint">{row.hint}</span>}
                  </span>
                  <span className="dice-pick-bonus">{signed(row.bonus)}</span>
                </button>
                <button type="button" className="dice-quick" aria-label={`Roll ${row.label} now, ${signed(row.bonus)}`} disabled={!canRoll} onClick={() => quick(row)}>
                  <DieGlyph />
                </button>
              </div>
            ))}
          </div>

          <SectionHead index="02" title="MECH" aside="In the cockpit. Mech skill checks." />
          <div className="dice-tiles">
            {rows.mech.map((row) => (
              <div key={row.key} className="dice-tile">
                <button type="button" className="dice-pick dice-tile-pick" aria-pressed={row.label === source} onClick={() => pick(row.label, row.bonus)}>
                  <span className="dice-tile-top">
                    <span className="dice-tile-name">{row.label}</span>
                    <span className="dice-tile-bonus">{signed(row.bonus)}</span>
                  </span>
                  <span className="dice-pick-hint">{row.hint}</span>
                </button>
                <button type="button" className="dice-tile-quick" aria-label={`Roll ${row.label} now, ${signed(row.bonus)}`} disabled={!canRoll} onClick={() => quick(row)}>
                  ROLL NOW
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHead({ index, title, aside }: { index: string; title: string; aside: string }) {
  return (
    <div className="dice-section-head">
      <span className="dice-section-index">{index}</span>
      <span className="dice-section-title">{title}</span>
      <span className="dice-section-aside">{aside}</span>
    </div>
  );
}
